/** @file dock-manager/BayRow.jsx — one bay lane rendering its bookings + overlays. */
import BookingCard from "./BookingCard";
import BufferBar from "./BufferBar";
import BlackoutBar from "./BlackoutBar";
import PatternGhost from "./PatternGhost";
import BlackoutOverlay from "./BlackoutOverlay";
import NowIndicator from "./NowIndicator";
import { fmtClock, fmtHour } from "./formatters";

export default function BayRow({
  bay,
  windowStart,
  windowEnd,
  pxPerHour,
  horizonHours,
  rowHeight,
  labelWidth,
  bookings,
  blackouts,
  patternGhosts,
  hourTicks,
  onSlotClick,
  onBookingClick,
  // PR-1 (buffer): client-level master switch + site default. BayRow computes
  // the effective per-bay buffer from these three signals so BufferBar can stay
  // dumb and just draw the strip.
  clientBufferEnabled = false,
  siteDefaultBuffer = 0,
  closed = [],
}) {
  const totalWidth = horizonHours * pxPerHour;
  return (
    <div
      className="flex border-b last:border-b-0"
      data-testid={`bay-row-${bay.id}`}
      style={{ height: rowHeight }}
    >
      {/* Sticky bay label */}
      <div
        className="sticky left-0 z-10 bg-white border-r flex items-center gap-2 px-3"
        style={{ width: labelWidth, minWidth: labelWidth }}
      >
        <span
          className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: bay.color || "#94a3b8" }}
        />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <div className="text-sm font-medium truncate">{bay.name}</div>
            {!bay.is_active && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600" data-testid={`dm-bay-inactive-${bay.id}`}>
                inactive
              </span>
            )}
          </div>
          {bay.code && (
            <div className="text-[11px] text-slate-500 tabular-nums truncate">{bay.code}</div>
          )}
        </div>
      </div>

      {/* Scrollable lane */}
      <div className="relative" style={{ width: totalWidth }}>
        {/* Hour separators (clickable slots) */}
        {hourTicks.slice(0, -1).map(({ idx }) => (
          <button
            type="button"
            key={idx}
            onClick={() => onSlotClick(bay.id, idx)}
            data-testid={`slot-${bay.id}-${idx}`}
            className="absolute top-0 bottom-0 hover:bg-blue-50/50 transition border-l first:border-l-0 border-slate-200"
            style={{ left: idx * pxPerHour, width: pxPerHour }}
            title={`Add booking · ${fmtHour(new Date(windowStart.getTime() + idx * 3600 * 1000))}`}
          />
        ))}

        {/* Closed hours / days (site ∩ bay hours, open days) — grey, clicks pass through */}
        {closed.map(([cs, ce]) => (
          <div
            key={`closed-${cs}`}
            className="absolute top-0 bottom-0 bg-slate-200/70 pointer-events-none"
            style={{ left: ((cs - windowStart.getTime()) / 3600000) * pxPerHour, width: ((ce - cs) / 3600000) * pxPerHour }}
            title="Closed"
            data-testid={`closed-${bay.id}-${cs}`}
          />
        ))}

        {/* Bay blackouts */}
        {blackouts.map((bk) => (
          <BlackoutBar
            key={bk.id}
            blackout={bk}
            windowStart={windowStart}
            windowEnd={windowEnd}
            pxPerHour={pxPerHour}
            rowHeight={rowHeight}
          />
        ))}

        {/* Pattern ghosts (recurring previews) — render BENEATH bookings */}
        {(patternGhosts || []).map((g) => (
          <PatternGhost
            key={g.id}
            ghost={g}
            windowStart={windowStart}
            windowEnd={windowEnd}
            pxPerHour={pxPerHour}
          />
        ))}

        {/* Booking cards + buffer bars (buffer rendered under card hover but
            visible between adjacent bookings) */}
        {bookings.map((bk) => (
          <BookingCard
            key={bk.id}
            booking={bk}
            bay={bay}
            windowStart={windowStart}
            windowEnd={windowEnd}
            pxPerHour={pxPerHour}
            onClick={() => onBookingClick(bk)}
          />
        ))}
        {clientBufferEnabled && bookings.map((bk) => (
          <BufferBar
            key={`buf-${bk.id}`}
            booking={bk}
            bufferMinutes={bay.buffer_minutes ?? siteDefaultBuffer ?? 0}
            windowStart={windowStart}
            windowEnd={windowEnd}
            pxPerHour={pxPerHour}
          />
        ))}
      </div>
    </div>
  );
}

